<h2
  mat-dialog-title
  class="!tw-text-[16px] !tw-font-semibold !tw-leading-[24px]"
>
  Share with others
</h2>
<mat-dialog-content
  class="tw-min-h-[160px]"
  rvSpinner="share-with-users-access-loader"
  [rvShowSpinner]="loadingAccessControlList"
>
  @if (users.apiError) {
    <div id="errorBox" class="alert alert-danger text-danger" role="alert">
      <strong>{{ users.errorMessage }}</strong> {{ users.apiError }}
    </div>
  }

  @if (accessControlListError) {
    <div class="alert alert-danger text-danger" role="alert">
      Unable to load the users who currently have access. Please try again.
    </div>
  }

  @if (!loadingAccessControlList && !accessControlListError) {
    <div
      class="tw-grid tw-grid-cols-[minmax(0,1fr)_max-content] tw-items-start tw-gap-[8px] tw-pt-[4px] max-[600px]:tw-grid-cols-1"
    >
      <div>
        <div
          cdkOverlayOrigin
          #searchOrigin="cdkOverlayOrigin"
          class="tw-flex tw-min-h-[40px] tw-w-full tw-flex-wrap tw-items-center tw-gap-[6px] tw-rounded-[6px] tw-border-[1px] tw-border-solid tw-border-gray-300 tw-bg-white tw-px-[10px] tw-py-[4px] focus-within:tw-border-[2px] focus-within:tw-border-[#008f68] focus-within:tw-px-[9px] focus-within:tw-py-[3px]"
          aria-label="Newly selected users"
        >
          @for (entry of newlyAddedUserAccess; track entry.id) {
            <span
              class="tw-inline-flex tw-h-[36px] tw-items-center tw-gap-[6px] tw-rounded-[6px] tw-bg-gray-100 tw-px-[8px] tw-text-gray-700"
            >
              <initials-badge
                class="tw-flex-none"
                [name]="userName(entry)"
                [size]="24"
              ></initials-badge>
              <span>{{ userName(entry) }}</span>
              <button
                type="button"
                class="tw-inline-flex tw-border-0 tw-bg-transparent tw-p-[4px]"
                aria-label="Remove {{ userName(entry) }}"
                (click)="removeUser(entry.id)"
              >
                <streamline-icon
                  name="close"
                  width="8"
                  height="8"
                ></streamline-icon>
              </button>
            </span>
          }
          <input
            class="tw-h-[30px] tw-min-w-[120px] tw-flex-1 tw-border-0 tw-bg-transparent tw-px-[4px] tw-text-[14px] tw-outline-none"
            [placeholder]="
              newlyAddedUserAccess.length ? '' : filterConfig.placeholder
            "
            [(ngModel)]="search.query"
            (ngModelChange)="searchUsers()"
            (focus)="focusSearch()"
            aria-label="Add users by email"
          />
        </div>

        <ng-template
          cdkConnectedOverlay
          [cdkConnectedOverlayOrigin]="searchOrigin"
          [cdkConnectedOverlayOpen]="showUserSuggestions"
          [cdkConnectedOverlayWidth]="
            searchOrigin.elementRef.nativeElement.offsetWidth
          "
          [cdkConnectedOverlayPush]="true"
          (overlayOutsideClick)="blurSearch()"
          (overlayKeydown)="closeSearchOnEscape($event)"
        >
          <div
            class="tw-max-h-[320px] tw-w-full tw-overflow-auto tw-rounded-b-[8px] tw-bg-white tw-shadow-[0_10px_20px_rgba(0,0,0,0.2)]"
            role="listbox"
            aria-label="Users"
          >
            @for (user of availableUsers; track user.username) {
              <button
                type="button"
                class="tw-flex tw-w-full tw-items-center tw-gap-[12px] tw-border-0 tw-bg-white tw-px-[16px] tw-py-[10px] tw-text-left hover:tw-bg-gray-100 focus-visible:tw-bg-gray-100"
                role="option"
                (click)="addUser(user)"
              >
                <initials-badge
                  [name]="user.firstName + ' ' + user.lastName"
                  [size]="36"
                ></initials-badge>
                <span class="tw-flex tw-min-w-0 tw-flex-col">
                  <strong>{{ user.firstName }} {{ user.lastName }}</strong>
                  <small
                    class="tw-overflow-hidden tw-text-ellipsis tw-text-gray-600"
                    >{{ user.email }}</small
                  >
                </span>
              </button>
            } @empty {
              <p class="tw-m-0 tw-p-[16px] tw-text-gray-600">
                No matching users
              </p>
            }
          </div>
        </ng-template>
      </div>

      <mat-form-field
        appearance="outline"
        subscriptSizing="dynamic"
        [style.width]="accessSelectWidth(newUserAccessLevel)"
      >
        <mat-select
          [ngModel]="newUserAccessLevel"
          [panelWidth]="null"
          aria-label="Access for newly added users"
          (ngModelChange)="updateNewUserAccessLevel($event)"
        >
          @for (accessLevel of accessLevels; track accessLevel) {
            <mat-option
              class="[&_.mdc-list-item__primary-text]:!tw-whitespace-nowrap"
              [value]="accessLevel"
            >
              {{ accessLevel }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>

    @if (showNotificationFields) {
      <div class="tw-mt-[20px] tw-flex tw-flex-col tw-gap-[12px]">
        <mat-checkbox [(ngModel)]="sendEmailNotification">
          Send email notification
        </mat-checkbox>
        @if (showCommentField) {
          <textarea
            rows="4"
            class="tw-box-border tw-min-h-[86px] tw-w-full tw-resize-none tw-rounded-[6px] tw-border-[1px] tw-border-solid tw-border-gray-300 tw-bg-white tw-p-[12px] tw-text-[14px] tw-leading-[20px] tw-text-gray-900 tw-outline-none placeholder:tw-text-gray-500 focus:tw-border-[2px] focus:tw-border-[#008f68] focus:tw-p-[11px]"
            placeholder="Write a comment"
            aria-label="Comment for shared users"
            [(ngModel)]="comment"
          ></textarea>
        }
      </div>
    }

    @if (existingUserAccess.length) {
      <section class="tw-mt-[20px]" aria-labelledby="users-with-access-title">
        <h3
          id="users-with-access-title"
          class="tw-m-0 tw-mb-[8px] tw-text-[14px] tw-font-semibold"
        >
          Users with access
        </h3>
        @for (entry of existingUserAccess; track entry.id) {
          <div
            class="tw-grid tw-min-h-[52px] tw-grid-cols-[minmax(0,1fr)_max-content] tw-items-center tw-gap-[12px] max-[600px]:tw-grid-cols-1"
          >
            <div class="tw-flex tw-min-w-0 tw-items-center tw-gap-[10px]">
              <initials-badge
                [name]="userName(entry)"
                [size]="28"
              ></initials-badge>
              <span class="tw-flex tw-min-w-0 tw-flex-col">
                <strong>{{ userName(entry) }}</strong>
              </span>
            </div>

            <mat-form-field
              appearance="outline"
              subscriptSizing="dynamic"
              [style.width]="accessSelectWidth(entry.accessLevel)"
            >
              <mat-select
                [ngModel]="entry.accessLevel"
                [panelWidth]="null"
                [attr.aria-label]="'Access for ' + userName(entry)"
                (selectionChange)="updateAccessLevel(entry, $event.value)"
              >
                <mat-select-trigger>
                  <span [class.tw-text-red-700]="!entry.accessLevel">
                    {{ entry.accessLevel || 'Remove Access' }}
                  </span>
                </mat-select-trigger>
                @for (accessLevel of accessLevels; track accessLevel) {
                  <mat-option
                    class="[&_.mdc-list-item__primary-text]:!tw-whitespace-nowrap"
                    [value]="accessLevel"
                  >
                    {{ accessLevel }}
                  </mat-option>
                }
                <rv-divider class="tw-my-[8px]" />
                <mat-option value="" class="tw-text-red-700">
                  Remove Access
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        }
      </section>
    }

    @if (saveError) {
      <div class="alert alert-danger text-danger tw-mt-[16px]" role="alert">
        Unable to save access changes. Please try again.
      </div>
    }
  }
</mat-dialog-content>
<mat-dialog-actions
  align="end"
  class="tw-border-0 tw-border-t-[1px] tw-border-solid tw-border-gray-200 tw-pt-[24px]"
>
  <button
    type="button"
    matButton="outlined"
    class="tw-h-[40px] !tw-w-[80px] !tw-min-w-[80px] !tw-max-w-[80px] tw-flex-none"
    data-dismiss="modal"
    [disabled]="saving"
    (click)="dismiss()"
  >
    Cancel
  </button>
  <button
    type="button"
    mat-flat-button
    class="tw-h-[40px] !tw-w-[120px] !tw-min-w-[120px] !tw-max-w-[120px] tw-flex-none"
    color="primary"
    [disabled]="
      loadingAccessControlList ||
      accessControlListError ||
      !hasChanges ||
      saving
    "
    (click)="apply()"
  >
    @if (saving) {
      <mat-spinner diameter="18" class="white"></mat-spinner>
      <span class="tw-sr-only">Saving access changes</span>
    } @else {
      Save
    }
  </button>
</mat-dialog-actions>
